import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getFeedbacks, getFeedbackDetails } from '../api';
import { Star, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const Feedback = () => {
    const { excludeCompany } = useOutletContext();
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [detailsMap, setDetailsMap] = useState({}); // Store details by optimize_id
    const [loadingDetails, setLoadingDetails] = useState({}); // Store loading state by optimize_id
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    useEffect(() => {
        fetchFeedbacks();
    }, [excludeCompany]);

    const fetchFeedbacks = async () => {
        setLoading(true);
        try {
            const res = await getFeedbacks(excludeCompany);
            if (res.data.success) {
                setFeedbacks(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch feedbacks", error);
        } finally {
            setLoading(false);
        }
    };

    const sortedFeedbacks = useMemo(() => {
        if (!feedbacks.length) return feedbacks;
        const sorted = [...feedbacks].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle null/undefined
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';

            // Handle numeric values
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // Handle string values
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [feedbacks, sortConfig]);

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const renderSortIcon = (key) => {
        const isActive = sortConfig.key === key;
        return (
            <div className="flex flex-col ml-1">
                <ChevronUp size={12} className={isActive && sortConfig.direction === 'asc' ? 'text-blue-600' : 'text-gray-300'} />
                <ChevronDown size={12} className={`-mt-1 ${isActive && sortConfig.direction === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
            </div>
        );
    };

    const handleRowClick = async (item) => {
        if (expandedId === item.id) {
            setExpandedId(null);
            return;
        }

        setExpandedId(item.id);

        // Fetch details if not already loaded
        if (!detailsMap[item.optimize_id] && item.optimize_id) {
            setLoadingDetails(prev => ({ ...prev, [item.optimize_id]: true }));
            try {
                const res = await getFeedbackDetails(item.optimize_id);
                if (res.data.success) {
                    setDetailsMap(prev => ({ ...prev, [item.optimize_id]: res.data.data }));
                }
            } catch (error) {
                console.error("Failed to fetch details", error);
            } finally {
                setLoadingDetails(prev => ({ ...prev, [item.optimize_id]: false }));
            }
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 B';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const calculateReduction = (orig, opt) => {
        if (!orig || !opt) return 0;
        return ((orig - opt) / orig * 100).toFixed(1);
    };

    const ComparisonCell = ({ orig, opt, formatFn, showBadge = true }) => {
        const reduction = calculateReduction(orig, opt);
        const isImproved = reduction > 0;

        return (
            <div className="flex flex-col items-center justify-center space-y-1">
                <div className="flex items-center space-x-2 text-sm">
                    <span className="text-gray-500">{formatFn ? formatFn(orig) : orig?.toLocaleString()}</span>
                    <ArrowRight size={14} className="text-gray-400" />
                    <span className="font-semibold text-gray-900">{formatFn ? formatFn(opt) : opt?.toLocaleString()}</span>
                </div>
                {showBadge && opt && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isImproved ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {isImproved ? `▼ ${reduction}%` : 'No Change'}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-800">User Feedback</h1>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('created_at')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">Date{renderSortIcon('created_at')}</div>
                                </th>
                                <th onClick={() => requestSort('username')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">User{renderSortIcon('username')}</div>
                                </th>
                                <th onClick={() => requestSort('rating')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">Rating{renderSortIcon('rating')}</div>
                                </th>
                                <th onClick={() => requestSort('orig_vertices')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">Vertices{renderSortIcon('orig_vertices')}</div>
                                </th>
                                <th onClick={() => requestSort('orig_polygons')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">Polygons{renderSortIcon('orig_polygons')}</div>
                                </th>
                                <th onClick={() => requestSort('orig_file_size')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">File Size{renderSortIcon('orig_file_size')}</div>
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Processing Time</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">Loading...</td>
                                </tr>
                            ) : sortedFeedbacks.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">No feedback found.</td>
                                </tr>
                            ) : (
                                sortedFeedbacks.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr
                                            onClick={() => handleRowClick(item)}
                                            className={`hover:bg-blue-50 transition-colors cursor-pointer ${expandedId === item.id ? 'bg-blue-50' : ''}`}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.created_at?.split(' ')[0]}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-900">{item.username}</span>
                                                    <span className="text-xs text-gray-500">{item.company}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <div className="flex items-center justify-center text-yellow-500 space-x-1">
                                                    <Star size={16} fill="currentColor" />
                                                    <span className="text-sm font-bold">{item.rating}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ComparisonCell orig={item.orig_vertices} opt={item.opt_vertices} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ComparisonCell orig={item.orig_polygons} opt={item.opt_polygons} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ComparisonCell orig={item.orig_file_size} opt={item.opt_file_size} formatFn={formatBytes} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col text-sm text-gray-600 space-y-1">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="w-16 text-xs font-medium text-gray-400">Analysis</span>
                                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">{item.analysis_time || '-'}</span>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="w-16 text-xs font-medium text-gray-400">Optimize</span>
                                                        <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">{item.optimization_time || '-'}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {expandedId === item.id ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                                            </td>
                                        </tr>
                                        {expandedId === item.id && (
                                            <tr className="bg-gray-50">
                                                <td colSpan="8" className="px-6 py-4">
                                                    <div className="bg-white rounded border border-gray-200 p-4">
                                                        <h4 className="font-semibold text-gray-700 mb-4 border-b pb-2">Feedback Details</h4>

                                                        {loadingDetails[item.optimize_id] ? (
                                                            <div className="flex justify-center py-4">
                                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                                                            </div>
                                                        ) : !detailsMap[item.optimize_id] || detailsMap[item.optimize_id].length === 0 ? (
                                                            <p className="text-gray-500 text-center py-2">No additional details.</p>
                                                        ) : (
                                                            <div className="space-y-4">
                                                                {detailsMap[item.optimize_id].map((detail, idx) => (
                                                                    <div key={idx} className="bg-gray-50 p-3 rounded border border-gray-100">
                                                                        <div className="mb-2">
                                                                            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                                                                                Q{detail.question_id}
                                                                            </span>
                                                                            <span className="ml-2 text-sm font-medium text-gray-800">
                                                                                {detail.question || `Question ${detail.question_id}`}
                                                                            </span>
                                                                        </div>
                                                                        <div className="pl-2 border-l-2 border-gray-200 ml-1">
                                                                            <p className="text-gray-700 whitespace-pre-wrap">{detail.answer}</p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Feedback;
